import { Image } from '@react-pdf/renderer';

import { Text } from '../../../components/pdf-export/Text/Text.js';
import { Tkv2MarketConditionsSummaryContent } from '../../marketConditions/Tkv2MarketConditionsSummaryContent.js';
import { Tkv2MarketConditionsExportDeps } from '../../marketConditions/types.js';
import { SummarySection } from '../SummarySection.js';
import { SummaryTable } from '../SummaryTable.js';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.js';
import { Tkv2PairedCostSummaryInterfaceV2 } from '../types.js';
import { PNG_HEIGHT, PNG_WIDTH } from '../utils/constants.js';
import { MarketConditionsAdjustmentExportTable } from './MarketConditionsAdjustmentExportTable.js';

export const MarketConditionsAdjustmentExport = (
  props: Tkv2PairedCostSummaryInterfaceV2 & {
    marketConditionsExportDeps: Tkv2MarketConditionsExportDeps;
    marketTrendsPngSource?: string;
  },
) => {
  const isZillow = !!props.housingTrends?.filteredHousingTrendsData.isZillow;
  const commentaryProps = props.exportableCommentaries.market;

  const title = getTitle(props, isZillow);
  const effectiveDate = getEffectiveDate(props);

  return (
    <SummarySection>
      <TitleAndExportToggleLockup
        {...props}
        title={title}
        id="isMarketConditionsCommentaryInExportSwitch"
        commentary={commentaryProps}
      />
      {isZillow && (
        <Text variant="body1">
          The Zillow Home Value Index provides a Typical Home Value, the weighted average of the
          middle third of homes in a given region. The date of sale adjustments below reflects the
          difference between the Typical Home Value of each month compared to the month of the
          effective date of the appraisal. If values have not been released for the current month,
          adjustments are based on the prior month.
        </Text>
      )}

      <Text variant="body1">{commentaryProps.commentary}</Text>
      {/* TODO: move other methods into here */}
      {props.marketConditions.dateOfSaleAdjustment.selectedMethod === 'marketTrends' ? (
        <Tkv2MarketConditionsSummaryContent
          exportDeps={props.marketConditionsExportDeps}
          state={props.marketConditions}
        />
      ) : (
        <>
          <MarketConditionsAdjustmentExportTable {...props} />
          {props.marketTrendsPngSource && (
            <SummaryTable>
              <Text variant="h5">{title}</Text>
              <Image
                src={props.marketTrendsPngSource}
                style={{ height: PNG_HEIGHT, width: PNG_WIDTH }}
              />
              <Text variant="h5">{effectiveDate}</Text>
            </SummaryTable>
          )}
        </>
      )}
    </SummarySection>
  );
};

function getTitle(props: Tkv2PairedCostSummaryInterfaceV2, isZillow: boolean): string {
  const {
    marketConditions: {
      dateOfSaleAdjustment: { selectedMethod, methods },
    },
    housingTrends,
  } = props;

  switch (selectedMethod) {
    case 'marketTrends': {
      if (methods?.marketTrends === undefined) return 'MLS Data';
      const {
        series: {
          meta: { geo },
        },
        using,
      } = methods.marketTrends;
      const usingStr = {
        p50closeprice: 'Median Close Price',
        p50closepricepersqft: 'Median Close Price per SqFt',
      }[using];
      return `MLS ${usingStr} (${geo})`;
    }
    case 'customAvgPricePerMonth':
      return 'Custom Typical Home Value';
    default:
      return isZillow
        ? `Zillow Typical Home Value (${housingTrends?.postalCode})`
        : `MLS Median Close Price (${housingTrends?.postalCode})`;
  }
}

function getEffectiveDate(props: Tkv2PairedCostSummaryInterfaceV2): string {
  const date =
    props.marketConditions.dateOfSaleAdjustment.selectedMethod === 'customAvgPricePerMonth'
      ? props.marketConditions.effectiveDate
      : props.housingTrends?.filteredHousingTrendsData.effectiveDate;

  return `Effective Date: ${date?.format('MMM dd, yyyy') ?? '(unknown)'}`;
}
